import React, {useCallback} from 'react';

import AnnotationPopover from './AnnotationPopover';
import {useAnnotationModeStore} from './AnnotationModeStore';
import {
  selectAnnotationNote,
  selectAnnotationType,
  selectHighlightColor
} from './annotationSelectors';
import type {AnnotationAnchor} from './annotationTypes';

interface AnnotationEditorProps {
  regionId: string;
  /** The mark being edited and the viewport rect its popover hangs from. */
  activeAnnotation: AnnotationAnchor;
  onClose: () => void;
  /** False where a mark cannot be an underline (a whole-supplement border). */
  allowUnderline?: boolean;
}

/**
 * Store-connected editor for one active mark: reads the mark's style and note
 * by (regionId, id) and applies edits through the store actions. Renders
 * nothing once the mark is gone.
 */
export default function AnnotationEditor({
  regionId,
  activeAnnotation,
  onClose,
  allowUnderline = true
}: AnnotationEditorProps) {
  const setHighlightColor = useAnnotationModeStore((state) => state.setHighlightColor);
  const setAnnotationUnderline = useAnnotationModeStore((state) => state.setAnnotationUnderline);
  const setAnnotationNote = useAnnotationModeStore((state) => state.setAnnotationNote);
  const removeAnnotation = useAnnotationModeStore((state) => state.removeAnnotation);
  const annotationId = activeAnnotation.id;
  const color = useAnnotationModeStore((state) =>
    selectHighlightColor(state, regionId, annotationId)
  );
  const type = useAnnotationModeStore((state) =>
    selectAnnotationType(state, regionId, annotationId)
  );
  const note = useAnnotationModeStore((state) =>
    selectAnnotationNote(state, regionId, annotationId)
  );
  const handleNoteChange = useCallback(
    (nextNote: string) => setAnnotationNote(regionId, annotationId, nextNote),
    [regionId, annotationId, setAnnotationNote]
  );

  if (!color || !type) {
    return null;
  }

  return (
    // Keyed so switching marks always remounts with that mark's own note draft.
    <AnnotationPopover
      key={annotationId}
      anchorRect={activeAnnotation.rect}
      selectedColor={color}
      selectedType={type}
      note={note ?? ''}
      allowUnderline={allowUnderline}
      onSelectColor={(nextColor) => setHighlightColor(regionId, annotationId, nextColor)}
      onSelectUnderline={() => setAnnotationUnderline(regionId, annotationId)}
      onNoteChange={handleNoteChange}
      onRemove={() => {
        removeAnnotation(regionId, annotationId);
        onClose();
      }}
      onDismiss={onClose}
    />
  );
}
